import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8MonthlySalesStack } from "../../p8OverviewData";
import { formatOverviewCurrency } from "../../p8OverviewData";

import {
  ChartHeadline,
  ChartLegend,
  ChartMonthAxisTick,
  ChartNavButtons,
  CHART_LEGEND_GAP_CLASS,
  CHART_MARGIN_WITH_AXIS_LABELS,
  CHART_Y_AXIS_WIDTH,
  chartXAxisLabel,
  formatChartAxisCurrency,
  type ChartLegendItem,
} from "./chartShared";
import { MonthlyAdSalesTooltip } from "./MonthlyAdSalesTooltip";

const STACK_COLORS = {
  previous: "#5D2E37",
  reorder: "#E3773E",
  new: "#93BBC9",
} as const;

const Y_AXIS_TICK_COUNT = 5;
/**
 * Round steps only, so ticks land on values that read cleanly whether they're
 * spelled out or abbreviated. The wider set the other charts use includes 1.5
 * and 3, which produced the $600 step behind labels like "$1k, $2k, $2k".
 */
const NICE_STEP_FACTORS = [1, 2, 2.5, 5] as const;
const BAR_TOP_RADIUS = 4;

/**
 * Ticks are spelled out in full ("$1,000") to match production, which the shared
 * abbreviating formatter can't do: rounding to whole thousands collapsed $1,800
 * and $2,400 into the same "$2k". Full dollars only fit the y-axis gutter up to
 * five figures, so advertisers billing more than that keep the abbreviation.
 */
const FULL_DOLLAR_TICK_CEILING = 10_000;

type MonthlyAdSalesBySourceChartProps = {
  data: P8MonthlySalesStack[];
};

/**
 * Stack order is previous, reorder, new, so the cap belongs to the highest of
 * those with any value. A static radius on `new` would leave a square top in any
 * month with no new-customer sales.
 */
function getTopSegmentKey(point: P8MonthlySalesStack): string {
  if (point.new > 0) {
    return "new";
  }
  if (point.reorder > 0) {
    return "reorder";
  }
  return "previous";
}

type BarShapeProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: P8MonthlySalesStack;
};

function createRoundedTopBar(dataKey: string) {
  return function RoundedTopBar({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill,
    payload,
  }: BarShapeProps) {
    if (width <= 0 || height <= 0) {
      return null;
    }
    const isTop = payload ? getTopSegmentKey(payload) === dataKey : false;
    const r = isTop ? Math.min(BAR_TOP_RADIUS, width / 2, height) : 0;
    const d = r
      ? `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${
          x + width - r
        },${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${
          y + height
        } Z`
      : `M${x},${y} L${x + width},${y} L${x + width},${y + height} L${x},${
          y + height
        } Z`;
    return <path d={d} fill={fill} />;
  };
}

function findNiceStepDollars(minStepDollars: number): number {
  for (let scale = 100; scale <= 1e12; scale *= 10) {
    for (const factor of NICE_STEP_FACTORS) {
      const candidate = scale * factor;
      if (candidate >= minStepDollars) {
        return candidate;
      }
    }
  }
  return 1e12;
}

function computeStackedYAxisLayout(data: P8MonthlySalesStack[]) {
  const dataMax = Math.max(
    0,
    ...data.map((point) => point.new + point.reorder + point.previous)
  );
  const target = Math.max(dataMax, 1000);
  const step = findNiceStepDollars(target / (Y_AXIS_TICK_COUNT - 1));
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

const LEGEND_ITEMS: ChartLegendItem[] = [
  {
    key: "new",
    label: "New customer ads",
    color: STACK_COLORS.new,
    tooltip: COPY.overview.charts.bySource.newAds,
  },
  {
    key: "reorder",
    label: "Reorder ads",
    color: STACK_COLORS.reorder,
    tooltip: COPY.overview.charts.bySource.reorderAds,
  },
  {
    key: "previous",
    label: "Previous months' ad acquisitions",
    color: STACK_COLORS.previous,
    tooltip: COPY.overview.charts.bySource.previousMonths,
  },
];

export function MonthlyAdSalesBySourceChart({
  data,
}: MonthlyAdSalesBySourceChartProps) {
  const { domain, ticks } = useMemo(
    () => computeStackedYAxisLayout(data),
    [data]
  );

  return (
    <div className="bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] p-6">
      <ChartHeadline
        title={COPY.overview.charts.bySource.title}
        description={COPY.overview.charts.bySource.description}
        actions={<ChartNavButtons />}
      />

      <div
        className={`mt-4 flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        <div className="min-h-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={CHART_MARGIN_WITH_AXIS_LABELS}
              barCategoryGap="30%"
              barSize={12}
            >
              <CartesianGrid
                stroke="#DFE0E1"
                strokeDasharray="3 0"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tickLine={{ stroke: "#DFE0E1" }}
                axisLine={{ stroke: "#DFE0E1" }}
                interval={0}
                height={44}
                tick={(props) => <ChartMonthAxisTick {...props} />}
                label={chartXAxisLabel("Order month")}
              />
              <YAxis
                domain={domain}
                ticks={ticks}
                interval={0}
                tick={{ fontSize: 11, fill: "#757575" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: number) =>
                  domain[1] < FULL_DOLLAR_TICK_CEILING
                    ? formatOverviewCurrency(value)
                    : formatChartAxisCurrency(value)
                }
                width={CHART_Y_AXIS_WIDTH}
              />
              <Tooltip
                shared={false}
                cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                content={(props) => <MonthlyAdSalesTooltip {...props} />}
              />
              <Bar
                dataKey="previous"
                stackId="sales"
                fill={STACK_COLORS.previous}
                shape={createRoundedTopBar("previous")}
                isAnimationActive={false}
              />
              <Bar
                dataKey="reorder"
                stackId="sales"
                fill={STACK_COLORS.reorder}
                shape={createRoundedTopBar("reorder")}
                isAnimationActive={false}
              />
              <Bar
                dataKey="new"
                stackId="sales"
                fill={STACK_COLORS.new}
                shape={createRoundedTopBar("new")}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <ChartLegend items={LEGEND_ITEMS} />
      </div>
    </div>
  );
}
